import { ActionType, ProColumns, ProFormSelect } from '@ant-design/pro-components';
import { DatePicker, Form, Select, Table } from 'antd';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { Filter } from 'base/ui/components/Icons';
import dayjs from 'dayjs';
import { usePermission } from 'hooks/usePermission';
import { observer } from 'mobx-react';
import { WasteHandoverRegistrationReport } from 'models/Report';
import { ExportButton } from 'pages/Components/FunctionButton';
import PageWrapper from 'pages/Components/PageWrapper';
import SaveReportForm from 'pages/Components/reportForm';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { useEffect, useRef, useState } from 'react';
import { HiSearch } from 'react-icons/hi';
import { CommonModel } from 'services/Main/common_services';
import { RowHazardous, SearchWasteHandoverRegistrationReport } from 'services/Main/report_services';
import { commonMasterDataPresenter, commonMasterDataStore, commonPresenter, commonStore, reportPresenter, reportStore, wasteGroupingEnvironmentPermitPresenter, wasteGroupingEnvironmentPermitStore } from 'stores/root_store';
import { DATE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants';
import { formatDecimalNumber } from 'utils/formatHelper';
import { checkPermissionWithId } from 'utils/permissionHelper';
import { saveSearchParams, getSearchParams } from 'utils/searchParamsHelper';
import { useResizableColumns } from 'utils/reportHelper';
import { removeEmptyValues } from 'utils/transformHelper';


const initialSearch: SearchWasteHandoverRegistrationReport = {
  paging: {
    pageSize: PAGE_SIZE_DEFAULT,
    pageIndex: 1,
    orderBy: undefined,
    orderByDesc: undefined,
  },
  deliveryDateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  deliveryDateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
}

// màn hình Báo cáo tổng hợp khối lượng chất thải công nghiệp thông thường & chất thải rắn sinh hoạt
const createChatThaiCongNghiep = () => {
  return observer(() => {
    document.title = 'Báo cáo - ' + SYSTEM_TITLE;
    usePermission('INDEX')
    const isExport = checkPermissionWithId("EXPORT")

    // ========== SEARCH =================
    const [searchForm] = Form.useForm()
    // mở drawer search
    const [openDrawer, setOpenDrawer] = useState(false)

    // cột bảng động
    const buildDynamicColumns = (apiCols: any[]): ProColumns<any>[] => {
      // cột tháng do BE trả
      const monthCols: ProColumns<any>[] = (apiCols || []).map((c) => ({
        title: c.title,               // "01/2026"
        dataIndex: c.dataIndex,       // "M_202601"
        key: c.dataIndex,
        width: 110,
        align: 'right',
        render: (v) => formatDecimalNumber(`${v}`),
      }));

      return [
        // ===== LEFT FIXED =====
        {
          title: 'STT',
          dataIndex: 'stt',
          key: 'stt',
          fixed: 'left',
          width: 50,
          align: 'center',
        },
        {
          title: 'Tên chất thải CNTT',
          dataIndex: 'ProductName',
          key: 'ProductName',
          fixed: 'left',
          width: 260,
        },
        {
          title: 'Mã nhóm chất thải',
          dataIndex: 'WasteGroupingEnvironmentPermitCode',
          key: 'WasteGroupingEnvironmentPermitCode',
          fixed: 'left',
          width: 140,
          align: 'center',
        },
        {
          title: 'Tên nhóm chất thải (CNTT)',
          dataIndex: 'WasteGroupingEnvironmentPermitName',
          key: 'WasteGroupingEnvironmentPermitName',
          fixed: 'left',
          width: 140,
        },
        // ===== GROUP DYNAMIC =====
        {
          title: 'Khối lượng (kg)',
          key: 'group_qty',
          align: 'center',
          children: monthCols,
        },

        // ===== RIGHT SIDE =====
        {
          title: 'Tổng theo loại (Kg)',
          dataIndex: 'TotalByTypeKg',
          key: 'TotalByTypeKg',
          width: 140,
          align: 'right',
          render: (v) => formatDecimalNumber(`${v}`),
        },
        {
          title: 'Khối lượng đăng ký GPMT (kg/Y)',
          dataIndex: 'GPMTYearQty',
          key: 'GPMTYearQty',
          width: 190,
          align: 'right',
          render: (v) => formatDecimalNumber(`${v}`),
        },
        // {
        //   title: 'ĐVT GPMT',
        //   dataIndex: 'UnitName',
        //   key: 'UnitName',
        //   width: 110,
        //   align: 'center',
        //   render: (v) => v ?? '-',
        // },
        {
          title: 'Ghi chú',
          dataIndex: 'Note',
          key: 'Note',
          width: 220,
        },
        {
          title: 'Nhà cung cấp',
          dataIndex: 'VendorName',
          key: 'VendorName',
          width: 260,
        },
      ];
    };

    const [columns, setColumns] = useState<ProColumns<any>[]>(buildDynamicColumns([]));

    const [dataPaging, setDataPaging] = useState<any>(initialSearch);
    const [dataTable, setDataTable] = useState<RowHazardous[] | undefined>(undefined)
    // disable nhập ngày khi chọn khác "Custom"
    const [disableDatePicker, setDisableDatePicker] = useState(false)
    const [loading, setLoading] = useState(true)
    const [apiMonthCols, setApiMonthCols] = useState<{ title: string; dataIndex: string }[]>([]);
    const [totalRow, setTotalRow] = useState<any>(null);


    const getListCommom = async () => {
      if (!commonStore.listCommonDate.length) {
        await commonPresenter.getListCommonDate(commonStore)
      }
    }
    // search báo cáo
    const getDataTable = async (payload: any) => {
      await reportPresenter.reportNoneHazardousWasteQuantityOverview(reportStore, payload);

      const res = reportStore.noneHazardousWasteQuantityOverviewRes;
      if (!res?.isSuccess) return [];

      const api = res.data; // { columns, rows, total, paging }

      setApiMonthCols(api?.columns || []);
      setTotalRow(api?.total || null);

      // ✅ set cột động
      setColumns(buildDynamicColumns(api?.columns || []));

      // ✅ map rows + STT
      const rows = (api?.rows || []).map((r: any, idx: number) => ({
        ...r,
        stt: (payload?.paging?.pageIndex - 1) * (payload?.paging?.pageSize) + (idx + 1),
      }));

      // ✅ nếu muốn thêm dòng TOTAL
      // const totalRow = api?.total ? { ...api.total, stt: 'Tổng' } : null;
      // const finalRows = totalRow ? [...rows, totalRow] : rows;

      setDataTable(rows);
      return rows;
    };



    const getDateByCommonDate = async (commonDate: string) => {
      setDisableDatePicker(true)
      await commonPresenter.getDateByCommonDate(commonStore, commonDate)
      if (commonStore.commonDate.fromDate) {
        searchForm.setFieldsValue({ deliveryDateFrom: dayjs(commonStore.commonDate.fromDate, "YYYY-MM-DD"), deliveryDateTo: dayjs(commonStore.commonDate.toDate, "YYYY-MM-DD") })
      }
    }



    useEffect(() => {
      Promise.allSettled([
        getListCommom(),
        commonPresenter.getDropdownWasteMaterialTaskStatus(commonStore),

      ]).then(async () => {
        // Kiểm tra xem có saved search params không
        const savedParams = getSearchParams('reportChatThaiCongNghiep');

        if (savedParams) {
          // Nếu có saved params, load lại trạng thái search trước đó
          console.log('Loading saved search params:', savedParams);

          let paramsToUse = { ...savedParams };

          // Nếu có common preset (khác 'Custom'), gọi lại API để lấy ngày hiện tại cho preset đó
          // Điều này đảm bảo "ThisMonth" luôn là tháng này, "Today" là hôm nay, v.v.
          if (savedParams.common && savedParams.common !== 'Custom') {
            await getDateByCommonDate(savedParams.common);
            // getDateByCommonDate đã set deliveryDateFrom/To vào searchForm
            // Lấy giá trị từ form để cập nhật paramsToUse
            const formValues = searchForm.getFieldsValue(['deliveryDateFrom', 'deliveryDateTo']);
            if (formValues.deliveryDateFrom) {
              paramsToUse = {
                ...paramsToUse,
                deliveryDateFrom: formValues.deliveryDateFrom.format('YYYY-MM-DD'),
                deliveryDateTo: formValues.deliveryDateTo.format('YYYY-MM-DD'),
              };
              // Lưu lại params với ngày đã cập nhật
              saveSearchParams('reportChatThaiCongNghiep', paramsToUse);
            }
          }

          setDataPaging(paramsToUse);

          // Convert string dates back to dayjs để hiển thị trong form
          const formValues: any = {
            ...paramsToUse,
            deliveryDateFrom: paramsToUse.deliveryDateFrom ? dayjs(paramsToUse.deliveryDateFrom) : undefined,
            deliveryDateTo: paramsToUse.deliveryDateTo ? dayjs(paramsToUse.deliveryDateTo) : undefined,
            common: paramsToUse.common || 'ThisMonth',
          };

          // Set form values
          searchForm.setFieldsValue(formValues);

          // Set disable state cho date picker
          setDisableDatePicker(paramsToUse.common && paramsToUse.common !== 'Custom');

          // Load data với params đã cập nhật
          await getDataTable(paramsToUse);
        } else {
          // Nếu không có saved params, set mặc định là "Tháng này"
          if (commonStore.listCommonDate.length) {
            await getDateByCommonDate('ThisMonth');
            searchForm.setFieldsValue({ common: 'ThisMonth' });

            // Lưu params mặc định
            const defaultParams = {
              ...initialSearch,
              common: 'ThisMonth',
            };
            saveSearchParams('reportChatThaiCongNghiep', defaultParams);
          }
        }

        setLoading(false)
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // =================================================================================================
    // mẫu báo cáo
    const tableRef = useRef<ActionType>();
    const constDataName = 'BCNguyHai';
    const {
      isResizablePresent,
      setIsResizablePresent,
      resizableColumnSelect,
      columnsState,
      handleColumnsStateChange,
      templateReportModalRef,
      userReports,
      selectedReportId,
      templateReportModalProps,
      handleResize,
    } = useResizableColumns<WasteHandoverRegistrationReport>(columns, setColumns, constDataName);


    const [wasteTypes, setWasteTypes] = useState<{ label: string; value: string }[]>([]);
    const [wasteGpmtOptions, setWasteGpmtOptions] = useState<{ label: string; value: string }[]>([]);
    const [wasteHdOptions, setWasteHdOptions] = useState<{ label: string; value: string }[]>([]);

    // Load dropdowns
    const loadWasteTypes = async () => {
      await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'WasteType');
      if (commonMasterDataStore.commonRespone?.isSuccess && commonMasterDataStore.commonRespone?.data) {
        const vendorTypes = commonMasterDataStore.commonRespone.data;
        setWasteTypes(vendorTypes.map((item: any) => ({ label: item.value, value: item.key })));
      }
    };

    const loadGpmt = async () => {
      await wasteGroupingEnvironmentPermitPresenter.searchWasteGroupingEnvironmentPermits(
        wasteGroupingEnvironmentPermitStore,
        {
          paging: {
            pageIndex: 1,
            pageSize: 99999,
            orderBy: '',
            orderByDesc: '',
          },
          activedOnly: false,
        }
      );
      if (wasteGroupingEnvironmentPermitStore.searchResponse.isSuccess) {
        setWasteGpmtOptions(
          (wasteGroupingEnvironmentPermitStore.searchResponse.data || []).map((item: any) => ({
            label: `${item.wasteGroupingEnvironmentPermitCustomCode ?? ''} | ${item.wasteGroupingEnvironmentPermitName ?? ''}`,
            value: item.wasteGroupingEnvironmentPermitId || '',
          }))
        );
      }
    };

    const loadHd = async () => {
      await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, false);
      setWasteHdOptions(
        (commonMasterDataStore.wasteGroupingContractOptions || []).map((item: any) => ({
          label: item.value,
          value: item.key,
        }))
      );
    };

    useEffect(() => {
      Promise.allSettled([
        loadWasteTypes(),
        loadGpmt(),
        loadHd(),
      ]).then(async () => {

      });
    }, []);



    const handleResetFilters = async () => {
      searchForm.resetFields();
      initialSearch.paging.pageIndex = 1;
      setDataPaging(initialSearch);
      searchForm.setFieldsValue({
        collectPreset: 'ThisMonth',
        deliveryDateFrom: dayjs().startOf('month'),
        deliveryDateTo: dayjs().endOf('month'),
      });
      setDisableDatePicker(true);
      searchForm.submit();
    };



    return (
      <PageWrapper
        breadcrumb={[{
          text: 'Báo cáo tổng hợp khối lượng chất thải công nghiệp thông thường & chất thải rắn sinh hoạt',
        }]}
        extras={
          <>
            {isExport && <ExportButton loading={loading} onClick={async () => {
              setLoading(true);
              const { paging, ...exportData } = dataPaging;
              await reportPresenter.exportNoneHazardousWasteQuantityOverview(exportData)
              setLoading(false);
            }} />}
            <CustomButton
              onClick={() => setOpenDrawer(true)}
              type="primary" color="#EFF4F9"
              className="!px-0 w-8"
            >
              <Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >
        <BoxContainer>
          <style>{`
                        .report-table .ant-table-tbody > tr:hover > td {
                            background: #fafafa;
                        }
                        .report-table .ant-table-summary > tr > td {
                            background: #E5E5E6 !important;
                        }
                    `}</style>
          <CustomProTable<any>
            bordered
            className="report-table"
            columns={columns}
            dataSource={dataTable}
            actionRef={tableRef}
            tableAlertRender={false}
            handleResize={handleResize}
            isResizablePresent={isResizablePresent}
            resizableColumnSelect={resizableColumnSelect}
            columnsState={{
              value: Object.keys(columnsState).length > 0 ? columnsState : undefined,
              onChange: (newColumnsState) => {
                handleColumnsStateChange(newColumnsState);
              },
            }}
            tableLayout='fixed'
            toolBarRender={() => [

              // nút lưu mẫu bc
              <SaveReportForm
                setIsResizablePresent={setIsResizablePresent}
                templateReportModalRef={templateReportModalRef}
                userReports={userReports}
                selectedReportId={selectedReportId}
                templateReportModalProps={templateReportModalProps}
              />,
            ]}
            rowKey="stt"
            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: reportStore.noneHazardousWasteQuantityOverviewRes?.paging?.recordsTotal,
            }}
            request={async (params = {}, sort) => {
              const pageIndex = (params as any)?.current ?? dataPaging.paging.pageIndex;
              const pageSize = (params as any)?.pageSize ?? dataPaging.paging.pageSize;

              const payload = {
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  pageIndex,
                  pageSize,
                  orderBy:
                    Object.values(sort)[0] === 'ascend'
                      ? `${Object.keys(sort)[0].split(',').pop()}`
                      : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend'
                      ? `${Object.keys(sort)[0].split(',').pop()}`
                      : undefined,
                },
              };

              // ✅ lưu lại để export/paging dùng chung
              setDataPaging(payload);

              const data = await getDataTable(payload);

              return {
                data,
                success: reportStore.noneHazardousWasteQuantityOverviewRes?.isSuccess,
                total: reportStore.noneHazardousWasteQuantityOverviewRes?.data?.paging?.totalCount,
              };
            }}

            summary={() => {
              if (!totalRow) return null;

              const leftFixedColSpan = 4; // STT + Tên  + Mã nhóm + Tên nhóm (đúng theo buildDynamicColumns của mình)
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    {/* Label bên trái */}
                    <Table.Summary.Cell index={0} colSpan={leftFixedColSpan}>
                      <span className="font-semibold text-right block">
                        Tổng theo tháng (kg)
                      </span>
                    </Table.Summary.Cell>

                    {/* Các cell tổng theo từng tháng (dynamic) */}
                    {apiMonthCols.map((c, i) => (
                      <Table.Summary.Cell key={c.dataIndex} index={leftFixedColSpan + i} align="right">
                        <span className="font-semibold text-right block">
                          {formatDecimalNumber(`${totalRow?.[c.dataIndex] ?? 0}`)}
                        </span>
                      </Table.Summary.Cell>
                    ))}

                    {/* Tổng theo loại (Kg) */}
                    <Table.Summary.Cell
                      index={leftFixedColSpan + apiMonthCols.length}
                      align="right"
                    >
                      <span className="font-semibold text-right block">
                        {formatDecimalNumber(`${totalRow?.TotalByTypeKg ?? 0}`)}
                      </span>
                    </Table.Summary.Cell>

                    {/* Khối lượng đăng ký GPMT (kg/Y) */}
                    <Table.Summary.Cell
                      index={leftFixedColSpan + apiMonthCols.length + 1}
                      align="right"
                    >
                      <span className="font-semibold text-right block">
                        {formatDecimalNumber(`${totalRow?.GPMTYearQty ?? 0}`)}
                      </span>
                    </Table.Summary.Cell>

                    {/* Các cột còn lại không cần tổng */}
                    <Table.Summary.Cell index={leftFixedColSpan + apiMonthCols.length + 2} />
                    <Table.Summary.Cell index={leftFixedColSpan + apiMonthCols.length + 3} />
                    <Table.Summary.Cell index={leftFixedColSpan + apiMonthCols.length + 4} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}

          />

        </BoxContainer>
        <SearchBox
          onClose={() => { setOpenDrawer(false) }}
          className="lg:!w-[50%]"
          open={openDrawer}
          footer={
            <div className='flex gap-2 justify-end'>
              <CustomButton
                onClick={() => {
                  handleResetFilters();
                  setOpenDrawer(false);
                }}
              >
                {'Reset tìm kiếm'}
              </CustomButton>
              <CustomButton
                htmlType="submit"
                // className="my-auto ml-auto"
                type="primary"
                icon={<HiSearch />}
                onClick={() => {
                  searchForm.submit();
                }}
              >
                Tìm kiếm
              </CustomButton>
            </div>
          }
        >
          <SearchForm
            form={searchForm}
            className=" !gap-y-4 !pb-24"
            onFinish={(values) => {
              const payload = {
                paging: { ...dataPaging.paging, pageIndex: 1 },
                ...removeEmptyValues({
                  wasteProductCategories: values?.wasteProductCategories || undefined,
                  wasteGroupingEnvironmentPermitCodes: values?.wasteGroupingEnvironmentPermitCodes || undefined,
                  wasteGroupingContractCodes: values?.wasteGroupingContractCodes || undefined,
                  wasteProductCodes: values?.wasteProductCodes || undefined,
                  vendorNumbers: values?.vendorNumbers || undefined,
                  deliveryDateFrom: values.deliveryDateFrom ? dayjs(values.deliveryDateFrom).format('YYYY-MM-DD') : undefined,
                  deliveryDateTo: values.deliveryDateTo ? dayjs(values.deliveryDateTo).format('YYYY-MM-DD') : undefined,
                  common: values.common,
                }),
              };
              console.log('valueeeeeeee', {
                values, payload
              });

              // Lưu search params vào localStorage
              saveSearchParams('reportChatThaiCongNghiep', payload);

              setDataPaging(payload);
              setOpenDrawer(false);
              tableRef.current?.reload();
            }}
          >

            {/* Nhóm chất thải */}
            <SearchItem name="wasteProductCategories" label={'Nhóm chất thải'}>
              <Select allowClear mode="multiple" options={wasteTypes} />
            </SearchItem>
            {/* Phân nhóm chất thải (GPMT) */}
            <SearchItem name="wasteGroupingEnvironmentPermitCodes" label={'Phân nhóm chất thải (GPMT)'}>
              <Select allowClear
                mode="multiple"
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                }
                options={wasteGpmtOptions} />
            </SearchItem>
            {/* Hợp đồng chất thải/ phế liệu */}
            <SearchItem name="wasteGroupingContractCodes" label={'Hợp đồng chất thải/ phế liệu'}>
              <Select allowClear
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                }
                mode="multiple"
                options={wasteHdOptions}
              />
            </SearchItem>
            {/* Chất thải/ phế liệu */}
            <SearchItem name="wasteProductCodes" label={'Chất thải/ phế liệu'}>
              <ProFormSelect
                allowClear
                mode="multiple"
                showSearch
                debounceTime={300}
                request={async ({ keyWords: keyword }) => {
                  const result = await commonPresenter.getDropdownProductWaste(keyword);
                  return (result.map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  })));
                }}
              />
            </SearchItem>

            {/* Nhà cung cấp */}
            <SearchItem name={'vendorNumbers'} label={'Nhà cung cấp'}>
              <ProFormSelect
                mode="multiple"
                request={async ({ keyWords: keyword }) => {
                  await commonPresenter.getDropdownVendor(commonStore, { keyword, takeNumber: 20, type: 'NCC_CT' });
                  return (commonStore.commonRespone.data.map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  })));
                }}
                allowClear showSearch

              />
            </SearchItem>


            <SearchItem name="common" initialValue={'ThisMonth'} label={'Thời gian bàn giao'} start>
              <Select options={commonStore.listCommonDate} fieldNames={{ label: 'value', value: 'key' }} onChange={async (value) => {
                if (value && value !== "Custom") {
                  getDateByCommonDate(value)
                } else {
                  setDisableDatePicker(false)
                  searchForm.setFieldsValue({ deliveryDateFrom: undefined, deliveryDateTo: undefined })
                }
              }} />
            </SearchItem>

            {/* Từ ngày */}
            <SearchItem
              initialValue={dayjs().startOf('month')}
              name={'deliveryDateFrom'} label={'Từ ngày'}
              start
              dependencies={['deliveryDateTo']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || getFieldValue('deliveryDateTo')) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('Chưa nhập giá trị "Từ ngày".'));;
                  },
                })
              ]}
            >
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
            {/* Đến ngày */}
            <SearchItem
              initialValue={dayjs().endOf('month')}
              name={'deliveryDateTo'} label={'Đến ngày'}
              dependencies={['deliveryDateFrom']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value || getFieldValue('deliveryDateFrom')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Chưa nhập giá trị "Đến ngày".'));
                  },
                })
              ]}
            >
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </PageWrapper>
    )
  })
}

export default createChatThaiCongNghiep